import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, UserCog, MapPin, Landmark, MapPinned } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fmtDate, Loading, Field } from "@/components/common";
import Section from "@/components/Section";

export default function AdminPersone({ preset }) {
  const [customers, setCustomers] = useState(null);
  const [pros, setPros] = useState([]);
  const [graves, setGraves] = useState([]);
  const [cemeteries, setCemeteries] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [terrForm, setTerrForm] = useState({ pro_id: "", comune: "", max_capacity: 100 });
  const [proForm, setProForm] = useState({ name: "", surname: "", email: "", password: "", phone: "" });
  const [cemForm, setCemForm] = useState({ name: "", comune: "", address: "", aerial_image: "", notes: "" });
  const [editingCem, setEditingCem] = useState(null);
  const [resetInfo, setResetInfo] = useState(null);

  const load = () => {
    api.get("/admin/customers").then((r) => setCustomers(r.data));
    api.get("/admin/professionals").then((r) => setPros(r.data));
    api.get("/admin/graves").then((r) => setGraves(r.data));
    api.get("/admin/cemeteries").then((r) => setCemeteries(r.data));
    api.get("/admin/territories").then((r) => setTerritories(r.data));
  };
  useEffect(load, []);

  useEffect(() => {
    if (preset?.focus && graves.length) {
      document.querySelector(`[data-testid="admin-grave-${preset.focus}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [preset, graves]);

  if (!customers) return <Loading />;

  const setP = (k) => (e) => setProForm({ ...proForm, [k]: e.target.value });
  const setC = (k) => (e) => setCemForm({ ...cemForm, [k]: e.target.value });

  const savePro = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/professionals", proForm);
      toast.success("Professionista aggiunto.");
      setProForm({ name: "", surname: "", email: "", password: "", phone: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveTerritory = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/territories", terrForm);
      toast.success("Comune assegnato in esclusiva.");
      setTerrForm({ pro_id: "", comune: "", max_capacity: 100 });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const resetPw = async (c) => {
    try {
      const r = await api.post(`/admin/customers/${c.id}/password-reset`);
      setResetInfo({ email: c.email, phone: r.data.phone || "", link: `${window.location.origin}${r.data.path}` });
      toast.success("Link di reimpostazione generato: monouso, valido 24 ore.");
    } catch (err) { toast.error(apiError(err)); }
  };

  const uploadGravePhoto = async (gid, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const up = await api.post("/uploads", fd);
      await api.patch(`/admin/graves/${gid}`, { admin_photo: up.data.url });
      toast.success("Foto di riferimento salvata: sarà visibile al professionista.");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveCem = async (e) => {
    e.preventDefault();
    try {
      if (editingCem) await api.patch(`/admin/cemeteries/${editingCem}`, cemForm);
      else await api.post("/admin/cemeteries", cemForm);
      toast.success("Cimitero salvato.");
      setCemForm({ name: "", comune: "", address: "", aerial_image: "", notes: "" });
      setEditingCem(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-persone">
      <Section title="Clienti" count={customers.length} icon={Users} testid="admin-persone-clienti" defaultOpen>
        <div className="card-soft overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-soft border-b border-[#e2e8f0]">
                <th className="p-4">Nome</th><th className="p-4">Email</th><th className="p-4">Telefono</th><th className="p-4">Luoghi</th><th className="p-4">Abbonamenti</th><th className="p-4">Dal</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id} className="border-b border-[#f1f5f9] last:border-0" data-testid={`admin-customer-${c.id}`}>
                  <td className="p-4 text-navy font-medium">{c.name} {c.surname}</td>
                  <td className="p-4">{c.email}</td>
                  <td className="p-4">{c.phone || "—"}</td>
                  <td className="p-4">{c.graves_count}</td>
                  <td className="p-4">{c.subscriptions_count}</td>
                  <td className="p-4">{fmtDate(c.created_at)}</td>
                  <td className="p-4"><button data-testid={`reset-pw-${c.id}`} onClick={() => resetPw(c)} className="text-xs font-semibold text-[#1e3a5f] hover:underline whitespace-nowrap">Reimposta password</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {resetInfo && (
          <div className="card-ivory p-4 mt-3 text-sm" data-testid="reset-link-box">
            <p className="font-semibold text-navy mb-1">Link di reimpostazione per {resetInfo.email}</p>
            <p className="text-xs text-muted-soft mb-2">Verifica l'identità del cliente, poi inviagli questo link (monouso, valido 24 ore). Nessuna password in chiaro: la nuova password la sceglie il cliente.</p>
            <p className="break-all text-xs bg-white rounded-lg border border-[#e2e8f0] px-3 py-2 mb-3" data-testid="reset-link-text">{resetInfo.link}</p>
            <div className="flex flex-wrap items-center gap-4">
              <button data-testid="reset-copy" onClick={() => { navigator.clipboard.writeText(resetInfo.link); toast.success("Link copiato."); }} className="text-xs font-semibold text-[#1e3a5f] hover:underline">Copia link</button>
              {resetInfo.phone && (
                <a data-testid="reset-send-whatsapp" target="_blank" rel="noreferrer"
                   href={`https://wa.me/${resetInfo.phone.replace(/\D/g, "").replace(/^(?!39)/, "39")}?text=${encodeURIComponent(`SEMPRE ACCANTO — Reimpostazione password. Apri questo link (monouso, valido 24 ore) e scegli la tua nuova password: ${resetInfo.link}`)}`}
                   className="text-xs font-semibold text-[#25D366] hover:underline">Invia via WhatsApp al cliente</a>
              )}
              <button data-testid="reset-close" onClick={() => setResetInfo(null)} className="text-xs text-muted-soft hover:underline">Chiudi</button>
            </div>
          </div>
        )}
      </Section>

      <Section title="Professionisti" count={pros.length} icon={UserCog} testid="admin-persone-pros">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mb-6">
          {pros.map((p) => (
            <div key={p.id} className="card-soft p-5" data-testid={`admin-pro-${p.id}`}>
              <p className="font-semibold text-navy">{p.name} {p.surname}</p>
              <p className="text-sm text-muted-soft">{p.email}{p.phone ? ` · ${p.phone}` : ""}</p>
              {p.active === false ? (
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5" data-testid={`pro-pending-${p.id}`}>In attesa di approvazione</span>
                  <button data-testid={`pro-approve-${p.id}`} onClick={async () => { try { await api.patch(`/admin/professionals/${p.id}`, { name: p.name, surname: p.surname, email: p.email, phone: p.phone, active: true }); toast.success("Professionista attivato."); load(); } catch (err) { toast.error(apiError(err)); } }} className="text-xs font-semibold text-[#1e3a5f] hover:underline">Approva e attiva</button>
                </div>
              ) : (
                <p className="text-xs font-semibold text-[#4a7c59] mt-1.5">Attivo</p>
              )}
              <p className="text-xs text-[#4a7c59] font-semibold mt-1">{p.assigned_count} interventi assegnati</p>
            </div>
          ))}
        </div>
        <form onSubmit={savePro} className="card-ivory p-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end" data-testid="pro-form">
          <Field label="Nome"><input data-testid="pro-name" className="input-soft" required value={proForm.name} onChange={setP("name")} /></Field>
          <Field label="Cognome"><input data-testid="pro-surname" className="input-soft" value={proForm.surname} onChange={setP("surname")} /></Field>
          <Field label="Email"><input data-testid="pro-email" type="email" className="input-soft" required value={proForm.email} onChange={setP("email")} /></Field>
          <Field label="Password iniziale"><input data-testid="pro-password" className="input-soft" value={proForm.password} onChange={setP("password")} placeholder="Pro2026!" /></Field>
          <Field label="Telefono"><input data-testid="pro-phone" className="input-soft" value={proForm.phone} onChange={setP("phone")} /></Field>
          <button data-testid="pro-save" type="submit" className="btn-primary !py-2.5 text-sm min-h-[44px] lg:col-span-5 sm:col-span-2">Aggiungi professionista</button>
        </form>
      </Section>

      <Section title="Assegnazioni territoriali" count={territories.length} icon={MapPinned} testid="admin-persone-territori">
        <p className="text-sm text-muted-soft mb-4">
          Ogni Comune può avere un solo professionista: l'assegnazione è esclusiva e copre tutti i cimiteri del Comune.
        </p>
        <div className="space-y-3 mb-6">
          {territories.length === 0 && <p className="text-sm text-muted-soft">Nessuna assegnazione attiva.</p>}
          {territories.map((t) => (
            <div key={t.id} className="card-soft p-5 flex flex-wrap items-center gap-x-6 gap-y-3 justify-between" data-testid={`territory-${t.id}`}>
              <div>
                <p className="font-semibold text-navy">{t.comune}</p>
                <p className="text-sm text-muted-soft">{t.pro_name} · {t.pro_email}</p>
                <p className={`text-xs font-semibold mt-1 ${t.active_clients >= t.max_capacity ? "text-red-700" : "text-[#4a7c59]"}`} data-testid={`capacity-label-${t.id}`}>
                  {t.active_clients}/{t.max_capacity} clienti attivi{t.active_clients >= t.max_capacity ? " · Capacità massima raggiunta" : ""}
                </p>
              </div>
              <div className="flex items-end gap-3">
                <div>
                  <label className="label-soft">Capacità max</label>
                  <input type="number" min="1" data-testid={`capacity-${t.id}`} className="input-soft !w-24 !py-2 text-sm min-h-[44px]" defaultValue={t.max_capacity}
                    onBlur={async (e) => { const v = parseInt(e.target.value, 10); if (v && v !== t.max_capacity) { try { await api.patch(`/admin/territories/${t.id}`, { max_capacity: v }); toast.success("Capacità aggiornata."); load(); } catch (err) { toast.error(apiError(err)); } } }} />
                </div>
                <button data-testid={`revoke-${t.id}`} onClick={async () => { try { await api.delete(`/admin/territories/${t.id}`); toast.success("Assegnazione revocata."); load(); } catch (err) { toast.error(apiError(err)); } }} className="text-xs font-semibold text-red-700 hover:underline pb-2 min-h-[44px]">Revoca esclusiva</button>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={saveTerritory} className="card-ivory p-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end" data-testid="territory-form">
          <Field label="Professionista">
            <select data-testid="territory-pro" className="input-soft" required value={terrForm.pro_id} onChange={(e) => setTerrForm({ ...terrForm, pro_id: e.target.value })}>
              <option value="">Scegli…</option>
              {pros.filter((p) => p.active !== false).map((p) => <option key={p.id} value={p.id}>{`${p.name} ${p.surname}`}</option>)}
            </select>
          </Field>
          <Field label="Comune">
            <select data-testid="territory-comune" className="input-soft" required value={terrForm.comune} onChange={(e) => setTerrForm({ ...terrForm, comune: e.target.value })}>
              <option value="">Scegli…</option>
              {[...new Set(cemeteries.map((c) => c.comune))].filter((cm) => !territories.some((t) => t.comune === cm)).map((cm) => <option key={cm} value={cm}>{cm}</option>)}
            </select>
          </Field>
          <Field label="Capacità max clienti">
            <input data-testid="territory-capacity" type="number" min="1" className="input-soft" value={terrForm.max_capacity} onChange={(e) => setTerrForm({ ...terrForm, max_capacity: parseInt(e.target.value, 10) || 100 })} />
          </Field>
          <button data-testid="territory-save" type="submit" className="btn-primary !py-2.5 text-sm min-h-[44px]">Assegna in esclusiva</button>
        </form>
      </Section>

      <Section title="Luoghi registrati" count={graves.length} icon={MapPin} testid="admin-persone-luoghi" defaultOpen={!!preset?.focus}>
        <div className="card-soft overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-soft border-b border-[#e2e8f0]">
                <th className="p-4">Defunto</th><th className="p-4">Tipo</th><th className="p-4">Cimitero</th><th className="p-4">Posizione</th><th className="p-4">Cliente</th><th className="p-4">Foto riferimento</th>
              </tr>
            </thead>
            <tbody>
              {graves.map((g) => (
                <tr key={g.id} className="border-b border-[#f1f5f9] last:border-0" data-testid={`admin-grave-${g.id}`}>
                  <td className="p-4 text-navy font-medium">{g.deceased_name} {g.deceased_surname}</td>
                  <td className="p-4 capitalize">{g.grave_type}</td>
                  <td className="p-4">{g.cemetery_name}</td>
                  <td className="p-4">Campo {g.campo || "—"}, fila {g.fila || "—"}, n. {g.numero || "—"}{g.position_confirmed ? " ✓" : ""}</td>
                  <td className="p-4">{g.customer_name}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {(g.admin_photo || g.photo) && <img src={g.admin_photo || g.photo} alt="Foto luogo" className="w-10 h-10 rounded-lg object-cover" data-testid={`grave-photo-${g.id}`} />}
                      <label className="text-xs font-semibold text-[#1e3a5f] hover:underline cursor-pointer whitespace-nowrap min-h-[44px] inline-flex items-center" data-testid={`grave-photo-upload-${g.id}`}>
                        {g.admin_photo ? "Sostituisci foto" : "Aggiungi foto"}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadGravePhoto(g.id, e.target.files?.[0])} />
                      </label>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Cimiteri" count={cemeteries.length} icon={Landmark} testid="admin-persone-cimiteri">
        <div className="grid gap-4 sm:grid-cols-2 mb-6">
          {cemeteries.map((c) => (
            <div key={c.id} className="card-soft overflow-hidden" data-testid={`admin-cemetery-${c.id}`}>
              {c.aerial_image && <img src={c.aerial_image} alt={c.name} className="w-full aspect-video object-cover" />}
              <div className="p-5">
                <p className="font-semibold text-navy">{c.name}</p>
                <p className="text-sm text-muted-soft">{c.comune} · {c.address}</p>
                <p className="text-xs text-muted-soft mt-1">{c.notes}</p>
                <p className="text-xs text-[#4a7c59] font-semibold mt-2">{c.graves_count} luoghi registrati</p>
                <div className="flex gap-3 mt-3">
                  <button data-testid={`edit-cemetery-${c.id}`} onClick={() => { setEditingCem(c.id); setCemForm({ name: c.name, comune: c.comune, address: c.address, aerial_image: c.aerial_image, notes: c.notes }); }} className="text-xs font-semibold text-[#1e3a5f] hover:underline min-h-[44px]">Modifica</button>
                  <button data-testid={`delete-cemetery-${c.id}`} onClick={async () => { try { await api.delete(`/admin/cemeteries/${c.id}`); load(); } catch (err) { toast.error(apiError(err)); } }} className="text-xs font-semibold text-red-700 hover:underline min-h-[44px]">Elimina</button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={saveCem} className="card-ivory p-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end" data-testid="cemetery-form">
          <Field label="Nome"><input data-testid="cem-name" className="input-soft" required value={cemForm.name} onChange={setC("name")} /></Field>
          <Field label="Comune"><input data-testid="cem-comune" className="input-soft" required value={cemForm.comune} onChange={setC("comune")} /></Field>
          <Field label="Indirizzo"><input data-testid="cem-address" className="input-soft" value={cemForm.address} onChange={setC("address")} /></Field>
          <Field label="URL immagine aerea/satellite"><input data-testid="cem-image" className="input-soft" value={cemForm.aerial_image} onChange={setC("aerial_image")} /></Field>
          <Field label="Note"><input data-testid="cem-notes" className="input-soft" value={cemForm.notes} onChange={setC("notes")} /></Field>
          <button data-testid="cem-save" type="submit" className="btn-primary !py-2.5 text-sm min-h-[44px] lg:col-span-5 sm:col-span-2">
            {editingCem ? "Salva modifiche" : "Aggiungi cimitero"}
          </button>
        </form>
      </Section>
    </div>
  );
}
